import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Bank, Plus, Trash, PencilSimple, ChartLine, HandGrabbing, ArrowRight } from "@phosphor-icons/react";

/**
 * v1.25.35 — UPI Mapping "Board".
 * Drag UPI chips between master cards (or from the pool). Click a chip to
 * open the Payments-by-UPI report filtered to that UPI, then use browser
 * back — or the "Back to UPI Mappings" pill — to return here.
 */
export default function UpiMappings() {
    const navigate = useNavigate();
    const [mappings, setMappings] = useState([]);
    const [companyUpis, setCompanyUpis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dragging, setDragging] = useState(null);  // { upi, from }
    const [dropTarget, setDropTarget] = useState(null); // mapping.id | "__pool__"
    const [busyIds, setBusyIds] = useState(new Set());
    const [editing, setEditing] = useState(null);
    const blank = () => ({ master_upi_id: "", label: "", notes: "" });
    const [form, setForm] = useState(blank());
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(() => new Set()); // lowercased upi ids ticked in the pool
    const [bulkMaster, setBulkMaster] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: m }, { data: u }] = await Promise.all([
                api.get("/upi-mappings"),
                api.get("/settings/upis").catch(() => ({ data: { upis: [] } })),
            ]);
            setMappings(m.mappings || []);
            setCompanyUpis(u.upis || u || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Every UPI already linked SOMEWHERE
    const linkedMap = useMemo(() => {
        const m = new Map();
        mappings.forEach((mp) => (mp.linked_upi_ids || []).forEach((u) => m.set(u.toLowerCase(), mp.id)));
        return m;
    }, [mappings]);

    // Pool = company UPIs not linked anywhere
    const pool = useMemo(() => {
        return companyUpis.filter((u) => !linkedMap.has(u.upi_id.toLowerCase()));
    }, [companyUpis, linkedMap]);

    const findUpiLabel = (upiId) => {
        const c = companyUpis.find((x) => x.upi_id.toLowerCase() === upiId.toLowerCase());
        return c?.label || "";
    };

    // ---------- Checkbox selection + bulk move (pool only) ----------
    const poolKeys = pool.map((u) => u.upi_id.toLowerCase());
    const allSelected = poolKeys.length > 0 && poolKeys.every((k) => selected.has(k));
    const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(poolKeys)));
    const toggleSelect = (upi) => setSelected((prev) => {
        const n = new Set(prev); const k = upi.toLowerCase();
        n.has(k) ? n.delete(k) : n.add(k);
        return n;
    });
    const clearSelection = () => { setSelected(new Set()); setBulkMaster(""); };
    const bulkMove = async () => {
        if (!bulkMaster) { toast.error("Pick a Master UPI to move into"); return; }
        const tgt = mappings.find((m) => m.id === bulkMaster);
        if (!tgt) return;
        const sel = pool.filter((u) => selected.has(u.upi_id.toLowerCase())).map((u) => u.upi_id);
        if (!sel.length) return;
        const existing = new Set((tgt.linked_upi_ids || []).map((x) => x.toLowerCase()));
        const newLinked = [...(tgt.linked_upi_ids || []), ...sel.filter((u) => !existing.has(u.toLowerCase()))];
        setBusyIds(new Set([bulkMaster]));
        try {
            await api.patch(`/upi-mappings/${bulkMaster}`, { linked_upi_ids: newLinked });
            toast.success(`Moved ${sel.length} UPI${sel.length === 1 ? "" : "s"} → ${tgt.master_upi_id}`);
            clearSelection();
            await load();
        } catch (e) { toast.error(formatApiError(e)); await load(); }
        finally { setBusyIds(new Set()); }
    };

    // ---------- DnD helpers ----------
    const onDragStart = (e, upi, fromMappingId) => {
        setDragging({ upi, from: fromMappingId });
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/json", JSON.stringify({ upi, from: fromMappingId }));
        // Visual: make the chip semi-transparent while dragging
        try { e.dataTransfer.setDragImage(e.currentTarget, 10, 10); } catch (_) {}
    };
    const onDragEnd = () => { setDragging(null); setDropTarget(null); };
    const onDragOver = (e, targetId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropTarget !== targetId) setDropTarget(targetId);
    };
    const onDragLeave = (e, targetId) => {
        if (dropTarget === targetId) setDropTarget(null);
    };

    // ---------- Move UPI between master / pool ----------
    const moveUpi = async (upi, fromMappingId, toMappingId) => {
        if (fromMappingId === toMappingId) return;
        // Guard: cannot detach a mapping's own master UPI without deleting the master
        if (fromMappingId) {
            const src = mappings.find((m) => m.id === fromMappingId);
            if (src && src.master_upi_id?.toLowerCase() === upi.toLowerCase()) {
                toast.error(`"${upi}" is the master of this card — delete the master card first if you want to remove it.`);
                return;
            }
        }
        // Guard: target already contains this UPI
        if (toMappingId) {
            const tgt = mappings.find((m) => m.id === toMappingId);
            if (tgt && (tgt.linked_upi_ids || []).some((x) => x.toLowerCase() === upi.toLowerCase())) return;
        }

        const busy = new Set(busyIds);
        if (fromMappingId) busy.add(fromMappingId);
        if (toMappingId) busy.add(toMappingId);
        setBusyIds(busy);
        try {
            // Optimistic UI update
            setMappings((prev) => prev.map((m) => {
                if (m.id === fromMappingId) return { ...m, linked_upi_ids: (m.linked_upi_ids || []).filter((x) => x.toLowerCase() !== upi.toLowerCase()) };
                if (m.id === toMappingId) return { ...m, linked_upi_ids: [...(m.linked_upi_ids || []), upi] };
                return m;
            }));
            const calls = [];
            if (fromMappingId) {
                const src = mappings.find((m) => m.id === fromMappingId);
                const newLinked = (src.linked_upi_ids || []).filter((x) => x.toLowerCase() !== upi.toLowerCase());
                calls.push(api.patch(`/upi-mappings/${fromMappingId}`, { linked_upi_ids: newLinked }));
            }
            if (toMappingId) {
                const tgt = mappings.find((m) => m.id === toMappingId);
                const newLinked = [...(tgt.linked_upi_ids || []), upi];
                calls.push(api.patch(`/upi-mappings/${toMappingId}`, { linked_upi_ids: newLinked }));
            }
            await Promise.all(calls);
            toast.success(toMappingId ? `Moved ${upi}` : `Unlinked ${upi}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
            await load();  // rollback
        } finally {
            setBusyIds(new Set());
        }
    };

    const onDrop = async (e, toMappingId) => {
        e.preventDefault();
        setDropTarget(null);
        let data;
        try { data = JSON.parse(e.dataTransfer.getData("application/json") || "{}"); }
        catch (_) { data = dragging || {}; }
        const upi = data.upi;
        const from = data.from ?? null;
        setDragging(null);
        if (!upi) return;
        const to = toMappingId === "__pool__" ? null : toMappingId;
        await moveUpi(upi, from, to);
    };

    // ---------- Master CRUD ----------
    const openCreate = () => { setForm(blank()); setEditing({}); };
    const openEdit = (m) => { setForm({ master_upi_id: m.master_upi_id, label: m.label || "", notes: m.notes || "" }); setEditing(m); };
    const save = async () => {
        if (!form.master_upi_id.trim()) { toast.error("Master UPI ID is required"); return; }
        const payload = { master_upi_id: form.master_upi_id.trim(), label: form.label.trim(), notes: form.notes.trim() };
        setBusy(true);
        try {
            if (editing?.id) {
                await api.patch(`/upi-mappings/${editing.id}`, payload);
                toast.success("Master updated");
            } else {
                await api.post("/upi-mappings", { ...payload, linked_upi_ids: [form.master_upi_id.trim()] });
                toast.success("Master created — drag UPI chips onto it");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete master "${m.master_upi_id}"?\nAll linked UPIs return to the Unlinked pool.\nOriginal payments stay intact.`)) return;
        try {
            await api.delete(`/upi-mappings/${m.id}`);
            toast.success("Master deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ---------- Click chip → transactions ----------
    const openTxns = (upi, mappingId) => {
        // If this chip is a master, prefer master filter; else pass raw upi.
        const src = mappingId ? mappings.find((m) => m.id === mappingId) : null;
        const isMaster = src && src.master_upi_id?.toLowerCase() === upi.toLowerCase();
        const params = new URLSearchParams();
        if (isMaster) params.set("master", src.id);
        else params.set("upi", upi);
        params.set("back", "/admin/upi-mappings");
        navigate(`/admin/payments-by-upi?${params.toString()}`);
    };

    return (
        <div className="space-y-6" data-testid="upi-mappings-page" onClick={() => { if (selected.size) setSelected(new Set()); }}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">UPI Account Mapping</h1>
                    <p className="mt-2 max-w-3xl text-sm text-slate-500">
                        Group several UPI IDs (e.g. full and masked forms of the same bank account) under one <b>Master UPI</b>. <b>Drag</b> any chip from the pool onto a master card — or between master cards — to re-link. <b>Click</b> any chip to open its transactions.
                    </p>
                </div>
                <Button data-testid="new-upi-mapping" onClick={openCreate} className="bg-indigo-700 text-white hover:bg-indigo-800">
                    <Plus size={14} className="mr-1" /> New Master UPI
                </Button>
            </div>

            {loading && <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">Loading…</div>}

            {/* Pool of unlinked UPIs */}
            <div
                data-testid="upi-pool"
                onDragOver={(e) => onDragOver(e, "__pool__")}
                onDragLeave={(e) => onDragLeave(e, "__pool__")}
                onDrop={(e) => onDrop(e, "__pool__")}
                className={`rounded-xl border-2 p-4 transition-colors ${dropTarget === "__pool__" ? "border-indigo-500 bg-indigo-50/60" : "border-dashed border-slate-300 bg-slate-50/60"}`}
            >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" data-testid="upi-select-all" checked={allSelected} onChange={(e) => { e.stopPropagation(); toggleAll(); }} disabled={pool.length === 0} className="h-4 w-4 cursor-pointer accent-indigo-600" />
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Unlinked UPI Pool</div>
                                <div className="text-xs text-slate-500">Tick chips to bulk-move, or drag a chip onto a master card</div>
                            </div>
                        </label>
                        {selected.size > 0 && <span data-testid="upi-selected-count" className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white">{selected.size} selected</span>}
                    </div>
                    <div className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700">{pool.length} available</div>
                </div>

                {selected.size > 0 && (
                    <div data-testid="upi-bulk-toolbar" onClick={(e) => e.stopPropagation()} className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                        <span className="text-xs font-semibold text-indigo-900">Move {selected.size} selected to:</span>
                        <select data-testid="upi-bulk-master-select" value={bulkMaster} onChange={(e) => setBulkMaster(e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs">
                            <option value="">Select Master UPI…</option>
                            {mappings.map((m) => <option key={m.id} value={m.id}>{m.master_upi_id}{m.label ? ` · ${m.label}` : ""}</option>)}
                        </select>
                        <Button data-testid="upi-bulk-move-btn" size="sm" disabled={!bulkMaster} onClick={bulkMove} className="h-8 bg-indigo-700 text-white hover:bg-indigo-800">Move</Button>
                        <button type="button" data-testid="upi-bulk-clear-btn" onClick={clearSelection} className="text-xs font-semibold text-slate-600 underline hover:text-slate-900">Clear Selection</button>
                    </div>
                )}
                <div className="flex flex-wrap gap-2 min-h-[42px]">
                    {pool.length === 0 && <div className="text-xs italic text-slate-400 py-2">All company UPIs are already linked. Add more under Administration → Company UPI IDs.</div>}
                    {pool.map((u) => (
                        <UpiChip
                            key={u.upi_id}
                            upi={u.upi_id}
                            label={u.label || ""}
                            onDragStart={(e) => onDragStart(e, u.upi_id, null)}
                            onDragEnd={onDragEnd}
                            onClick={() => openTxns(u.upi_id, null)}
                            variant="pool"
                            selectable
                            selected={selected.has(u.upi_id.toLowerCase())}
                            onToggleSelect={() => toggleSelect(u.upi_id)}
                        />
                    ))}
                </div>
            </div>

            {/* Master cards grid */}
            {!loading && mappings.length === 0 && (
                <div className="rounded-md border border-slate-200 bg-white p-8 text-center">
                    <Bank size={32} className="mx-auto mb-2 text-slate-300" weight="duotone" />
                    <div className="text-sm text-slate-500">No master UPIs yet. Click <b>New Master UPI</b> above to create your first bank-account bundle.</div>
                </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {mappings.map((m) => {
                    const linked = (m.linked_upi_ids || []);
                    const isDropTarget = dropTarget === m.id;
                    const isBusy = busyIds.has(m.id);
                    return (
                        <div
                            key={m.id}
                            data-testid={`master-card-${m.id}`}
                            onDragOver={(e) => onDragOver(e, m.id)}
                            onDragLeave={(e) => onDragLeave(e, m.id)}
                            onDrop={(e) => onDrop(e, m.id)}
                            className={`relative rounded-xl border-2 bg-white shadow-sm transition-all ${isDropTarget ? "border-indigo-500 shadow-indigo-100 ring-4 ring-indigo-100" : "border-slate-200"} ${isBusy ? "opacity-60" : ""}`}
                        >
                            {/* Header */}
                            <div className="flex items-start gap-3 border-b border-slate-100 p-4">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow">
                                    <Bank size={22} weight="duotone" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <button
                                        type="button"
                                        onClick={() => openTxns(m.master_upi_id, m.id)}
                                        data-testid={`master-open-${m.id}`}
                                        className="block truncate text-left font-mono text-sm font-bold text-slate-900 hover:text-indigo-700 hover:underline"
                                        title="Open transactions for this master"
                                    >{m.master_upi_id}</button>
                                    <div className="mt-0.5 flex items-center gap-1.5">
                                        <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-700">MASTER</span>
                                        {m.label && <span className="truncate text-[11px] font-medium text-slate-600">{m.label}</span>}
                                    </div>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                    <button type="button" data-testid={`edit-master-${m.id}`} onClick={() => openEdit(m)} className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700" title="Edit master details">
                                        <PencilSimple size={12} weight="bold" />
                                    </button>
                                    <button type="button" data-testid={`report-master-${m.id}`} onClick={() => openTxns(m.master_upi_id, m.id)} className="rounded-md border border-emerald-200 bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100" title="Report / transactions">
                                        <ChartLine size={12} weight="bold" />
                                    </button>
                                    <button type="button" data-testid={`del-master-${m.id}`} onClick={() => remove(m)} className="rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50" title="Delete master">
                                        <Trash size={12} weight="bold" />
                                    </button>
                                </div>
                            </div>

                            {/* Linked UPI chips */}
                            <div className="p-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                        Linked UPIs · {linked.length}
                                    </div>
                                    {isDropTarget && (
                                        <div className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                            <ArrowRight size={10} weight="bold" /> Drop here
                                        </div>
                                    )}
                                </div>
                                <div className={`flex flex-wrap gap-1.5 min-h-[54px] rounded-md p-2 ${isDropTarget ? "bg-indigo-50/50" : "bg-slate-50/40"}`}>
                                    {linked.length === 0 && <div className="text-[11px] italic text-slate-400 py-2 px-1">Drag UPI chips from the pool onto this card</div>}
                                    {linked.map((u) => (
                                        <UpiChip
                                            key={u}
                                            upi={u}
                                            label={findUpiLabel(u)}
                                            isMaster={u.toLowerCase() === m.master_upi_id?.toLowerCase()}
                                            onDragStart={(e) => onDragStart(e, u, m.id)}
                                            onDragEnd={onDragEnd}
                                            onClick={() => openTxns(u, m.id)}
                                            variant="master"
                                        />
                                    ))}
                                </div>
                                <div className="mt-3 flex items-center gap-1 text-[10px] text-slate-400">
                                    <HandGrabbing size={11} weight="bold" /> Hold &amp; drag chips to move · Click to open transactions
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Create / Edit dialog — only edits master identity now */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent data-testid="upi-mapping-dialog" className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
                    <DialogHeader className="p-6 pb-3 border-b border-slate-100 shrink-0">
                        <DialogTitle>{editing?.id ? "Edit Master UPI" : "New Master UPI"}</DialogTitle>
                        <DialogDescription>
                            Create a Master UPI card. After saving, <b>drag</b> UPI chips from the pool onto the card to link them.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 overflow-y-auto px-6 py-4 flex-1 min-h-0">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Master UPI ID *</Label>
                            <Input data-testid="master-upi-input" value={form.master_upi_id} onChange={(e) => setForm({ ...form, master_upi_id: e.target.value })} placeholder="9811579000@ptsbi" className="mt-1 font-mono" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Label / Bank Account Name</Label>
                            <Input data-testid="upi-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="SBI Current – Delhi" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Notes</Label>
                            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional internal notes" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter className="p-6 pt-3 border-t border-slate-100 shrink-0">
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button data-testid="save-mapping-btn" onClick={save} disabled={busy || !form.master_upi_id.trim()} className="bg-indigo-700 text-white hover:bg-indigo-800">
                            {busy ? "Saving…" : (editing?.id ? "Save changes" : "Create master")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** Draggable UPI chip. */
function UpiChip({ upi, label, isMaster, onDragStart, onDragEnd, onClick, variant, selectable, selected, onToggleSelect }) {
    const base = isMaster
        ? "border-indigo-600 bg-indigo-600 text-white"
        : selected
            ? "border-indigo-500 bg-indigo-100 text-indigo-900 ring-2 ring-indigo-400"
            : variant === "master"
                ? "border-indigo-200 bg-white text-indigo-800 hover:border-indigo-400"
                : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300";
    return (
        <span
            draggable
            data-testid={`upi-chip-${upi}`}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onClick}
            className={`group relative inline-flex cursor-grab items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing ${base}`}
            title={`Drag to move · Click to open transactions${label ? "\nLabel: " + label : ""}`}
        >
            {selectable && (
                <input
                    type="checkbox"
                    data-testid={`upi-select-${upi}`}
                    checked={!!selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
                    className="h-3.5 w-3.5 cursor-pointer accent-indigo-600"
                />
            )}
            <span className="text-slate-400 group-hover:text-indigo-500"><HandGrabbing size={10} weight="bold" /></span>
            <span className="truncate max-w-[160px]">{upi}</span>
            {label && !isMaster && <span className="rounded bg-slate-100 px-1 py-0 text-[9px] text-slate-500 group-hover:bg-indigo-50">{label}</span>}
            {isMaster && <span className="rounded bg-white/25 px-1 py-0 text-[8px] font-bold uppercase tracking-wider">MASTER</span>}
        </span>
    );
}
